import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { apiCall } from '../services/api';
import toast from 'react-hot-toast';
import PaginatedTable from './PaginatedTable';
import { useCache } from '../hooks/useCache';
import { useAuth } from '../contexts/AuthContext';
import { STORAGE_CLASSES, StorageClassOption } from '../constants/storageClasses';

interface User {
  userId: string;
  role: string;
}

interface Bucket {
  bucketName: string;
  description: string;
}

interface Permission {
  list: boolean;
  download: boolean;
  preview: boolean;
  upload: boolean;
  delete: boolean;
  maxObjectSize: number;
  monthlyDataTransferCap: number;
  // Storage class configuration — optional, backward-compatible
  allowedStorageClasses?: string[];
  defaultStorageClass?: string;
  showStorageClassSelector?: boolean;
}

interface UserPermission {
  userId: string;
  bucketName: string;
  actualBucketName?: string;
  folderPath?: string | null;
  displayName?: string;
  permissions: Permission;
  updatedAt: string;
}

interface BucketTemplate {
  templateId: string;
  name: string;
  permissions: Permission;
}

interface FolderInfo {
  path: string;
  name: string;
  depth?: number;
}

const PermissionManagement: React.FC = () => {
  const { isManager, loading: authLoading } = useAuth();
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedBucket, setSelectedBucket] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedFolderPath, setSelectedFolderPath] = useState<string>('');
  const [enableFolderRestriction, setEnableFolderRestriction] = useState(false);
  const [bucketFolders, setBucketFolders] = useState<FolderInfo[]>([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [permissions, setPermissions] = useState<Permission>({
    list: false,
    download: false,
    preview: false,
    upload: false,
    delete: false,
    maxObjectSize: 0,
    monthlyDataTransferCap: 0
  });
  const [allowedStorageClasses, setAllowedStorageClasses] = useState<string[]>(['STANDARD']);
  const [defaultStorageClass, setDefaultStorageClass] = useState<string>('STANDARD');
  const [showStorageClassSelector, setShowStorageClassSelector] = useState<boolean>(false);
  const [editingPermission, setEditingPermission] = useState<UserPermission | null>(null);
  const [deletingPermission, setDeletingPermission] = useState<{ userId: string, bucketName: string } | null>(null);

  // Use cache for users data
  const {
    data: users = [],
    loading: usersLoading,
  } = useCache(
    'admin-users-permissions',
    async () => {
      const response = await apiCall('/admin/users');
      return Array.isArray(response) ? response : [];
    },
    { ttl: 60 * 1000 }
  );

  // Use cache for buckets data
  const {
    data: buckets = [],
    loading: bucketsLoading,
  } = useCache(
    'admin-buckets-permissions',
    async () => {
      const response = await apiCall('/admin/buckets');
      return Array.isArray(response) ? response : [];
    },
    { ttl: 60 * 1000 }
  );

  // Use cache for user permissions
  const {
    data: userPermissions = [],
    loading: permissionsLoading,
    refresh: refreshPermissions
  } = useCache(
    'admin-user-permissions',
    async () => {
      const response = await apiCall('/admin/permissions');
      return Array.isArray(response) ? response : [];
    },
    { ttl: 30 * 1000 }
  );

  // Use cache for bucket templates
  const {
    data: bucketTemplates = [],
    loading: templatesLoading,
  } = useCache(
    'admin-bucket-templates-permissions',
    async () => {
      const response = await apiCall('/admin/bucket-templates');
      return Array.isArray(response) ? response : [];
    },
    { ttl: 60 * 1000 }
  );

  const loading = usersLoading || bucketsLoading || permissionsLoading || templatesLoading || authLoading;

  // Load folder structure when bucket is selected
  useEffect(() => {
    if (selectedBucket && enableFolderRestriction) {
      loadBucketFolders(selectedBucket);
    } else {
      setBucketFolders([]);
    }
  }, [selectedBucket, enableFolderRestriction]);

  const loadBucketFolders = async (bucketName: string) => {
    setLoadingFolders(true);
    try {
      // Task 24.4: wrap in Promise.race with 10-second timeout
      const timeoutPromise = new Promise<{ folders: FolderInfo[] }>((resolve) =>
        setTimeout(() => resolve({ folders: [] }), 10000)
      );
      const response = await Promise.race([
        apiCall(`/admin/bucket-folders/${bucketName}`),
        timeoutPromise
      ]);
      setBucketFolders(Array.isArray(response?.folders) ? response.folders : []);
    } catch (error) {
      console.error('Error loading bucket folders:', error);
      toast.error('Failed to load folder structure');
      setBucketFolders([]);
    } finally {
      setLoadingFolders(false);
    }
  };

  const applyTemplate = (templateId: string) => {
    const template = (bucketTemplates || []).find((t: BucketTemplate) => t.templateId === templateId);
    if (template) {
      if (isManager && template.permissions.delete) {
        toast.error('Managers cannot apply templates with delete permissions');
        return;
      }
      
      setPermissions({
        ...template.permissions,
        delete: isManager ? false : (template.permissions.delete || false)
      });
      setSelectedTemplate(templateId);
      toast.success(`Applied template: ${template.name}`);
    }
  };

  const savePermissions = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isManager && permissions.delete) {
      toast.error('Managers cannot grant delete permissions');
      return;
    }

    // Validate folder path if folder restriction is enabled
    if (enableFolderRestriction && !selectedFolderPath) {
      toast.error('Please select a folder or disable folder restriction');
      return;
    }

    // Check for conflicting permissions (frontend validation for better UX)
    if (!editingPermission) {
      const existingForBucket = (userPermissions || []).filter((p: UserPermission) => {
        const actualBucket = p.actualBucketName || p.bucketName.split('#')[0];
        return p.userId === selectedUser && actualBucket === selectedBucket;
      });

      if (existingForBucket.length > 0) {
        const hasBucketLevel = existingForBucket.some((p: UserPermission) => !p.folderPath);
        const hasFolderLevel = existingForBucket.some((p: UserPermission) => p.folderPath);

        if (enableFolderRestriction && hasBucketLevel) {
          toast.error('Cannot add folder-level permission: User already has bucket-level access. Remove it first to restrict to specific folders.');
          return;
        }

        if (!enableFolderRestriction && hasFolderLevel) {
          toast.error('Cannot add bucket-level permission: User already has folder-level access. Remove folder permissions first to grant full bucket access.');
          return;
        }
      }
    }
    
    // Storage class validation (Requirements 10.1, 10.2)
    let finalAllowedStorageClasses = allowedStorageClasses;
    let finalDefaultStorageClass = defaultStorageClass;
    if (finalAllowedStorageClasses.length === 0) {
      finalAllowedStorageClasses = ['STANDARD'];
      finalDefaultStorageClass = 'STANDARD';
    } else if (!finalDefaultStorageClass || !finalAllowedStorageClasses.includes(finalDefaultStorageClass)) {
      toast.error('Default class must be one of the allowed classes.');
      return;
    }

    try {
      await apiCall('/admin/permissions', {
        method: 'POST',
        body: JSON.stringify({
          userId: selectedUser,
          bucketName: selectedBucket,
          folderPath: enableFolderRestriction ? selectedFolderPath : null,
          permissions: {
            ...permissions,
            list: true, // Always required
            delete: isManager ? false : permissions.delete,
            allowedStorageClasses: finalAllowedStorageClasses,
            defaultStorageClass: finalDefaultStorageClass,
            showStorageClassSelector,
          }
        })
      });

      const successMessage = enableFolderRestriction 
        ? `Permissions updated for folder: ${selectedFolderPath}`
        : 'Permissions updated for entire bucket';
      toast.success(successMessage);

      // Reset form
      resetForm();
      refreshPermissions();
    } catch (error: any) {
      console.error('Error saving permissions:', error);
      // Show the specific error message from backend if available
      const errorMessage = error?.message || 'Failed to save permissions';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setSelectedUser('');
    setSelectedBucket('');
    setSelectedTemplate('');
    setSelectedFolderPath('');
    setEnableFolderRestriction(false);
    setBucketFolders([]);
    setEditingPermission(null);
    setPermissions({
      list: false,
      download: false,
      preview: false,
      upload: false,
      delete: false,
      maxObjectSize: 0,
      monthlyDataTransferCap: 0
    });
    setAllowedStorageClasses(['STANDARD']);
    setDefaultStorageClass('STANDARD');
    setShowStorageClassSelector(false);
  };

  const editPermission = (userPermission: UserPermission) => {
    setEditingPermission(userPermission);
    setSelectedUser(userPermission.userId);
    
    // Handle folder-level permissions
    const actualBucket = userPermission.actualBucketName || userPermission.bucketName.split('#')[0];
    const folderPath = userPermission.folderPath || 
      (userPermission.bucketName.includes('#') ? userPermission.bucketName.split('#')[1] : null);
    
    setSelectedBucket(actualBucket);
    
    if (folderPath) {
      setEnableFolderRestriction(true);
      setSelectedFolderPath(folderPath);
      loadBucketFolders(actualBucket);
    } else {
      setEnableFolderRestriction(false);
      setSelectedFolderPath('');
    }
    
    setPermissions({
      ...userPermission.permissions,
      delete: userPermission.permissions.delete || false,
      // Records predating the download/preview split have no preview field
      preview: userPermission.permissions.preview ?? userPermission.permissions.download ?? false
    });
    setAllowedStorageClasses(userPermission.permissions.allowedStorageClasses ?? ['STANDARD']);
    setDefaultStorageClass(userPermission.permissions.defaultStorageClass ?? 'STANDARD');
    setShowStorageClassSelector(userPermission.permissions.showStorageClassSelector ?? false);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingPermission) return;

    try {
      // URL encode the bucketName which may contain # for folder paths
      const encodedBucketName = encodeURIComponent(deletingPermission.bucketName);
      await apiCall(`/admin/permissions/${deletingPermission.userId}/${encodedBucketName}`, {
        method: 'DELETE'
      });

      toast.success('Permission removed successfully');
      setDeletingPermission(null);
      refreshPermissions();
    } catch (error) {
      console.error('Error deleting permission:', error);
      toast.error('Failed to remove permission');
      setDeletingPermission(null);
    }
  };

  const cancelEdit = () => {
    resetForm();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-6">Permission Management</h2>

        {/* Manager Access Notice */}
        {isManager && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Manager Access:</strong> You can assign permissions to users but cannot grant delete permissions. 
                  Templates with delete permissions are hidden from selection.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Permission Form */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingPermission ? 'Edit Permission' : 'Assign New Permission'}
          </h3>

          <form onSubmit={savePermissions} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label htmlFor="perm-user-select" className="block text-sm font-medium text-gray-700">User</label>
                <select
                  id="perm-user-select"
                  value={selectedUser}
                  onChange={(e) => setSelectedUser(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!!editingPermission}
                >
                  <option value="">Select a user</option>
                  {(users || []).map((user: User) => (
                    <option key={user.userId} value={user.userId}>
                      {user.userId} ({user.role})
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="perm-bucket-select" className="block text-sm font-medium text-gray-700">Bucket</label>
                <select
                  id="perm-bucket-select"
                  value={selectedBucket}
                  onChange={(e) => {
                    setSelectedBucket(e.target.value);
                    setSelectedFolderPath('');
                  }}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  required
                  disabled={!!editingPermission}
                >
                  <option value="">Select a bucket</option>
                  {(buckets || []).map((bucket: Bucket) => (
                    <option key={bucket.bucketName} value={bucket.bucketName}>
                      {bucket.bucketName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="perm-template-select" className="block text-sm font-medium text-gray-700">Apply Template</label>
                <select
                  id="perm-template-select"
                  value={selectedTemplate}
                  onChange={(e) => applyTemplate(e.target.value)}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Choose template...</option>
                  {(bucketTemplates || [])
                    .filter((template: BucketTemplate) => !isManager || !template.permissions.delete)
                    .map((template: BucketTemplate) => (
                    <option key={template.templateId} value={template.templateId}>
                      {template.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Folder Restriction Section */}
            {selectedBucket && (
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                <div className="flex items-center mb-3">
                  <input
                    type="checkbox"
                    id="enableFolderRestriction"
                    checked={enableFolderRestriction}
                    onChange={(e) => {
                      setEnableFolderRestriction(e.target.checked);
                      if (!e.target.checked) {
                        setSelectedFolderPath('');
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <label htmlFor="enableFolderRestriction" className="ml-2 text-sm font-medium text-gray-700">
                    Restrict to specific folder
                  </label>
                  <span className="ml-2 text-xs text-gray-500">
                    (User will only have access to the selected folder and its contents)
                  </span>
                </div>

                {enableFolderRestriction && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Folder
                    </label>
                    {loadingFolders ? (
                      <div className="flex items-center text-sm text-gray-500">
                        <svg className="animate-spin h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading folders...
                      </div>
                    ) : bucketFolders.length === 0 ? (
                      <div className="text-sm text-gray-500 bg-yellow-50 p-3 rounded border border-yellow-200">
                        <div className="flex items-center">
                          <svg className="h-5 w-5 text-yellow-400 mr-2" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                          No folders found in this bucket. Create folders first or grant bucket-level access.
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <select
                          value={selectedFolderPath}
                          onChange={(e) => setSelectedFolderPath(e.target.value)}
                          className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                          required={enableFolderRestriction}
                        >
                          <option value="">Select a folder...</option>
                          {bucketFolders.map((folder) => (
                            <option key={folder.path} value={folder.path}>
                              {'  '.repeat(folder.depth || 0)}📁 {folder.path}
                            </option>
                          ))}
                        </select>
                        {selectedFolderPath && (
                          <div className="text-xs text-green-600 flex items-center">
                            <svg className="h-4 w-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            User will have access to: {selectedBucket}/{selectedFolderPath}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">Permissions</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { key: 'list', label: 'Can List Files', required: true },
                  { key: 'preview', label: 'Can Preview', required: false },
                  { key: 'download', label: 'Can Download', required: false },
                  { key: 'upload', label: 'Can Upload', required: false },
                  { key: 'delete', label: 'Can Delete Objects', required: false }
                ].filter(({ key }) => !isManager || key !== 'delete').map(({ key, label, required }) => (
                  <label key={key} className={`flex items-center ${required ? 'bg-blue-50 p-2 rounded border border-blue-200' : ''}`}>
                    <input
                      type="checkbox"
                      checked={required ? true : (permissions[key as keyof Permission] as boolean)}
                      onChange={(e) => {
                        // Don't allow unchecking required permissions
                        if (required && !e.target.checked) return;
                        setPermissions({
                          ...permissions,
                          [key]: e.target.checked
                        });
                      }}
                      disabled={required}
                      className={`rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 ${required ? 'cursor-not-allowed' : ''}`}
                    />
                    <span className="ml-2 text-sm text-gray-700">
                      {label}
                      {required && <span className="ml-1 text-xs text-blue-600">(Required)</span>}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="perm-max-size" className="block text-sm font-medium text-gray-700">Max Object Size (MB)</label>
                <input
                  id="perm-max-size"
                  type="number"
                  value={permissions.maxObjectSize}
                  onChange={(e) => setPermissions({
                    ...permissions,
                    maxObjectSize: parseInt(e.target.value) || 0
                  })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  placeholder="0 = no limit"
                />
              </div>

              <div>
                <label htmlFor="perm-monthly-cap" className="block text-sm font-medium text-gray-700">Monthly Data Transfer Cap (GB)</label>
                <input
                  id="perm-monthly-cap"
                  type="number"
                  value={permissions.monthlyDataTransferCap}
                  onChange={(e) => setPermissions({
                    ...permissions,
                    monthlyDataTransferCap: parseInt(e.target.value) || 0
                  })}
                  className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  placeholder="0 = no limit"
                />
              </div>
            </div>

            {/* Storage Class Configuration */}
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Storage Class Configuration</h4>
              <p className="text-xs text-gray-500 mb-4">
                Control which S3 storage classes this user may use for uploads.
              </p>

              {/* Allowed classes checklist */}
              <div className="space-y-2 mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Allowed Storage Classes</label>
                {STORAGE_CLASSES.map((option: StorageClassOption) => (
                  <label
                    key={option.value}
                    htmlFor={`sc-check-${option.value}`}
                    className="flex items-start space-x-3 p-2 rounded-md hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      id={`sc-check-${option.value}`}
                      type="checkbox"
                      checked={allowedStorageClasses.includes(option.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAllowedStorageClasses(prev => [...prev, option.value]);
                        } else {
                          setAllowedStorageClasses(prev => prev.filter(c => c !== option.value));
                          if (defaultStorageClass === option.value) {
                            setDefaultStorageClass('');
                          }
                        }
                      }}
                      className="mt-0.5 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <div className="flex-1 min-w-0">
                      <span className="text-sm font-medium text-gray-800">{option.label}</span>
                      <p className="text-xs text-gray-500 mt-0.5">{option.description}</p>
                      {option.costHint && (
                        <p className="text-xs text-amber-600 mt-0.5">💰 {option.costHint}</p>
                      )}
                      {option.warning && (
                        <p className="text-xs text-red-600 mt-0.5">⚠️ {option.warning}</p>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              {/* Default class dropdown */}
              <div className="mb-4">
                <label htmlFor="sc-default-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Default Class
                </label>
                <select
                  id="sc-default-select"
                  value={defaultStorageClass}
                  onChange={(e) => setDefaultStorageClass(e.target.value)}
                  disabled={allowedStorageClasses.length === 0}
                  className="block w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {allowedStorageClasses.length === 0 ? (
                    <option value="">Select at least one class above first</option>
                  ) : (
                    <>
                      <option value="">Select a default class...</option>
                      {STORAGE_CLASSES.filter((sc: StorageClassOption) => allowedStorageClasses.includes(sc.value)).map((sc: StorageClassOption) => (
                        <option key={sc.value} value={sc.value}>{sc.label}</option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* Show selector toggle */}
              <div className="flex items-center space-x-3">
                <input
                  id="sc-selector-toggle"
                  type="checkbox"
                  checked={showStorageClassSelector}
                  onChange={(e) => setShowStorageClassSelector(e.target.checked)}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                />
                <label htmlFor="sc-selector-toggle" className="text-sm font-medium text-gray-700">
                  Show class selector to user
                </label>
                <span className="text-xs text-gray-500">(If unchecked, user uploads silently use the default class)</span>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Saving...
                  </>
                ) : (
                  editingPermission ? 'Update Permission' : 'Save Permission'
                )}
              </button>
              {editingPermission && (
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Cancel Edit
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      {/* Existing Permissions Table */}
      <div className="space-y-4">
        <div>
          <h3 className="text-lg leading-6 font-medium text-gray-900">Current Permissions</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Manage user access to S3 buckets and folders
          </p>
        </div>

        <PaginatedTable
          data={userPermissions || []}
          columns={[
            {
              key: 'userId',
              header: 'User',
              sortable: true,
              render: (userPermission: UserPermission) => (
                <div>
                  <p className="text-sm font-medium text-gray-900">{userPermission.userId}</p>
                  <div className="flex items-center text-sm text-gray-500">
                    {userPermission.folderPath ? (
                      <>
                        <span className="text-blue-600">📁</span>
                        <span className="ml-1">
                          {userPermission.actualBucketName || userPermission.bucketName.split('#')[0]}
                          <span className="text-blue-600 font-medium">/{userPermission.folderPath}</span>
                        </span>
                      </>
                    ) : (
                      <>
                        <span className="text-gray-400">📦</span>
                        <span className="ml-1">{userPermission.actualBucketName || userPermission.bucketName}</span>
                        <span className="ml-1 text-xs text-gray-400">(entire bucket)</span>
                      </>
                    )}
                  </div>
                </div>
              )
            },
            {
              key: 'scope',
              header: 'Scope',
              sortable: true,
              render: (userPermission: UserPermission) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                  userPermission.folderPath 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {userPermission.folderPath ? '📁 Folder' : '📦 Bucket'}
                </span>
              )
            },
            {
              key: 'permissions',
              header: 'Permissions',
              render: (userPermission: UserPermission) => (
                <div className="flex flex-wrap gap-1">
                  {userPermission.permissions?.list && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      List
                    </span>
                  )}
                  {(userPermission.permissions?.preview ?? userPermission.permissions?.download) && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-teal-100 text-teal-800">
                      Preview
                    </span>
                  )}
                  {userPermission.permissions?.download && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Download
                    </span>
                  )}
                  {userPermission.permissions?.upload && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Upload
                    </span>
                  )}
                  {userPermission.permissions?.delete && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      Delete
                    </span>
                  )}
                </div>
              )
            },
            {
              key: 'limits',
              header: 'Limits',
              render: (userPermission: UserPermission) => (
                <div className="text-xs text-gray-500">
                  {(userPermission.permissions?.maxObjectSize ?? 0) > 0 && (
                    <div>Max: {userPermission.permissions.maxObjectSize}MB</div>
                  )}
                  {(userPermission.permissions?.monthlyDataTransferCap ?? 0) > 0 && (
                    <div>Cap: {userPermission.permissions.monthlyDataTransferCap}GB/month</div>
                  )}
                </div>
              )
            },
            {
              key: 'updatedAt',
              header: 'Updated',
              sortable: true,
              render: (userPermission: UserPermission) => (
                <span className="text-sm text-gray-500">
                  {new Date(userPermission.updatedAt).toLocaleDateString()}
                </span>
              )
            }
          ]}
          loading={permissionsLoading}
          pageSize={10}
          searchPlaceholder="Search permissions by user, bucket, folder..."
          emptyMessage="No permissions configured"
          actions={(userPermission: UserPermission) => (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => editPermission(userPermission)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Edit
              </button>
              <button
                onClick={() => setDeletingPermission({ 
                  userId: userPermission.userId, 
                  bucketName: userPermission.bucketName 
                })}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Remove
              </button>
            </div>
          )}
        />
      </div>

      {/* Delete Confirmation Modal */}
      {deletingPermission && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="relative mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg leading-6 font-medium text-gray-900 mt-4">Remove Permission</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500">
                  Are you sure you want to remove this permission for user{' '}
                  <span className="font-medium text-gray-900">{deletingPermission.userId}</span>?
                </p>
                <p className="text-sm text-gray-500 mt-2">
                  {deletingPermission.bucketName.includes('#') ? (
                    <>
                      Folder: <span className="font-medium text-blue-600">{deletingPermission.bucketName.split('#')[1]}</span>
                      <br />
                      in bucket: <span className="font-medium">{deletingPermission.bucketName.split('#')[0]}</span>
                    </>
                  ) : (
                    <>Bucket: <span className="font-medium">{deletingPermission.bucketName}</span> (entire bucket)</>
                  )}
                </p>
              </div>
              <div className="flex space-x-4 mt-4">
                <button
                  onClick={() => setDeletingPermission(null)}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteConfirm}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
                >
                  Remove
                </button>
              </div>
            </div>
          </div>
        </div>
      , document.body)}
    </div>
  );
};

export default PermissionManagement;
